import type { CompanyOptionRepository } from "../../../domain/repositories/company/company-option-repository";
import type {
  CreateOptionRequest,
  OptionDetailResponse,
  CreateCombinationRequest,
  CreatePriceTierRequest,
} from "../../../domain/types/option";
import {
  OptionDetailResponseSchema,
} from "../../../domain/types/option";
import { companyOptionApiService } from "../../api/company/company-option-api-service";

export class CompanyOptionRepositoryImpl implements CompanyOptionRepository {
  async getOptions(companyId: number): Promise<OptionDetailResponse[]> {
    const response = await companyOptionApiService.getOptions(companyId);

    if (response.data) {
      return response.data.map((item) => OptionDetailResponseSchema.parse(item));
    }

    throw new Error(response.errorMsg || "옵션 목록 가져오기 실패.");
  }

  async getOptionDetail(companyId: number, optionId: number): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.getOptionDetail(companyId, optionId);

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "옵션 상세 가져오기 실패.");
  }

  async createOption(
    companyId: number,
    request: CreateOptionRequest,
  ): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.createOption(companyId, request);

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "옵션 생성 실패.");
  }

  async deleteOption(companyId: number, optionId: number): Promise<void> {
    await companyOptionApiService.deleteOption(companyId, optionId);
  }

  async createCombination(
    companyId: number,
    optionId: number,
    request: CreateCombinationRequest,
  ): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.createCombination(
      companyId,
      optionId,
      request,
    );

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "조합 생성 실패.");
  }

  async deleteCombination(
    companyId: number,
    optionId: number,
    combinationId: number,
  ): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.deleteCombination(
      companyId,
      optionId,
      combinationId,
    );

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "조합 삭제 실패.");
  }

  async bulkCreateCombinationKeys(
    companyId: number,
    optionId: number,
    combinationId: number,
    combinations: { attributeId: number; valueId: number }[][],
  ): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.bulkCreateCombinationKeys(
      companyId,
      optionId,
      combinationId,
      { combinations },
    );

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "조합키 일괄 생성 실패.");
  }

  async bulkCreatePriceTiers(
    companyId: number,
    optionId: number,
    combinationId: number,
    keyId: number,
    priceTiers: CreatePriceTierRequest[],
  ): Promise<OptionDetailResponse> {
    const response = await companyOptionApiService.bulkCreatePriceTiers(
      companyId,
      optionId,
      combinationId,
      keyId,
      { priceTiers },
    );

    if (response.data) {
      return OptionDetailResponseSchema.parse(response.data);
    }

    throw new Error(response.errorMsg || "가격 구간 일괄 생성 실패.");
  }
}
